import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as lessonActions from 'Actions/lesson';
import { MainAppReducer } from 'Types';
import { Box, Button, MenuItem, TextField } from '@mui/material';
import { CustomSwitch } from 'Components';
import Play from 'assets/images/Play.png';
import Pause from 'assets/images/Pause.png';
import Fullscreen from 'Components/Fullscreen';
import { LessonReducer } from 'Reducers/lessonReducer';
import React, { Dispatch, SetStateAction } from 'react';
import { Settings } from '@mui/icons-material';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';

const MAX_TRANSPOSE_SEMITONES = 7;
const TRANSPOSE_SEMITONE_OPTIONS = Array.from(
  { length: MAX_TRANSPOSE_SEMITONES * 2 + 1 },
  (_, index) => index - MAX_TRANSPOSE_SEMITONES,
);
const MODE_CONVERSION_OPTIONS: Array<{ value: ParallelModeConversion; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'to_minor', label: 'To Minor' },
  { value: 'to_major', label: 'To Major' },
];
const LESSON_FOOTER_SELECT_MENU_Z_INDEX = 5000;

const getTransposeLabel = (semitoneSteps: number): string => {
  if (semitoneSteps === 0) {
    return 'Concert (0)';
  }
  return `${semitoneSteps > 0 ? '+' : ''}${semitoneSteps}`;
};

const transposeSelectSx = {
  width: '105px',
  '& .MuiOutlinedInput-root': {
    height: '30px',
    color: '#FFFFFF',
    backgroundColor: '#243056',
    borderRadius: '6px',
    fontFamily: 'Lato',
    fontSize: '13px',
    fontWeight: 600,
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 30px 4px 8px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#525969',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#6F87F1',
  },
  '& .MuiSvgIcon-root': {
    color: '#FFFFFF',
    right: '5px',
  },
} as const;

const transposeSelectMenuProps = {
  sx: {
    zIndex: LESSON_FOOTER_SELECT_MENU_Z_INDEX,
  },
  PaperProps: {
    sx: {
      zIndex: LESSON_FOOTER_SELECT_MENU_Z_INDEX + 1,
      mt: '4px',
      color: '#ECECED',
      backgroundColor: '#10192E',
      border: '1px solid #3E4C73',
      borderRadius: '8px',
      boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
      '& .MuiMenuItem-root': {
        minHeight: '32px',
        fontFamily: 'Lato',
        fontSize: '13px',
        fontWeight: 600,
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(111, 135, 241, 0.32)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(111, 135, 241, 0.22)',
      },
    },
  },
} as const;

type FooterContainerProps = {
  setCursorIsVisible: (cursorIsVisible: boolean) => void;
  cursorIsVisible: boolean;
  playButtonActive?: boolean;
  debugShowTierSummary?: boolean; // NEW
  setDebugShowTierSummary?: (v: boolean) => void; // NEW
  freePlay: boolean;
  setFreePlay: Dispatch<SetStateAction<boolean>>;
  mobile: boolean;
  //setShowSettings: (showSettings: boolean) => void;
  //settingsShown: boolean;
  onPlayButtonOverlapChange?: (overlap: number) => void;
  toggleSettings: () => void;
  showTransposeControls?: boolean;
  transposeSemitones?: number;
  onTransposeChange?: (nextSemitones: number) => void;
  modeConversion?: ParallelModeConversion;
  onModeConversionChange?: (nextMode: ParallelModeConversion) => void;
};

const FooterContainer = (props: FooterContainerProps) => {
  const dispatch = useDispatch();
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const lesson = useSelector((state: LessonReducer) => state.lessonReducer);
  const playButtonRef = React.useRef<HTMLDivElement>(null);

  const { userData } = data;

  const playLevel = () => {
    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: !lesson.lessonPlaying }));
  };

  const { freePlay, setFreePlay } = props;

  const {
    cursorIsVisible,
    setCursorIsVisible,
    playButtonActive = true,
    debugShowTierSummary = false,
    setDebugShowTierSummary,
    mobile,
    onPlayButtonOverlapChange,
    toggleSettings,
    showTransposeControls = false,
    transposeSemitones = 0,
    onTransposeChange,
    modeConversion = 'off',
    onModeConversionChange,
  } = props;

  const hiddenStyles =
    lesson.lessonPlaying && mobile
      ? {
          transform: 'translateY(40px)',
        }
      : {
          transform: 'none',
        };

  const playButtonHiddenStyles =
    lesson.lessonPlaying && mobile
      ? {
          transform: 'translateY(50px)',
        }
      : {
          transform: 'none',
        };

  // Calculate play button overlap with canvas
  React.useEffect(() => {
    if (!onPlayButtonOverlapChange || mobile) {
      return;
    }

    const calculateOverlap = () => {
      const playButton = playButtonRef.current;
      if (!playButton) return;

      const buttonRect = playButton.getBoundingClientRect();

      // Find the main app container (canvas area)
      const mainAppContainer = document.querySelector('.main-app-container-styles');
      if (!mainAppContainer) return;

      const containerRect = mainAppContainer.getBoundingClientRect();

      // Calculate how much the button overlaps into the canvas from the bottom
      // The button is positioned absolutely at the bottom right
      // We need to check how much of the button's top portion overlaps into the canvas
      const buttonTop = buttonRect.top;
      const containerBottom = containerRect.bottom;

      // If button top is above container bottom, it's overlapping into the canvas
      const overlap = Math.max(0, containerBottom - buttonTop);

      // Add 10px buffer as requested
      const overlapWithBuffer = overlap > 0 ? overlap + 10 : 0;

      onPlayButtonOverlapChange(overlapWithBuffer);
    };

    // Calculate on mount and when relevant styles change
    calculateOverlap();

    // Recalculate on resize
    window.addEventListener('resize', calculateOverlap);

    return () => {
      window.removeEventListener('resize', calculateOverlap);
    };
  }, [onPlayButtonOverlapChange, mobile, lesson.lessonPlaying]);

  const controlsStyles2 = {
    // height: '54px',
    // border: '4px solid #525969',
    marginLeft: '25px',
    borderRadius: '50%',
    '&:hover': {
      background: 'rgba(222,144,118,.5)',
      cursor: 'pointer',
      transition: '0.3s',
      borderRadius: '50%',
      boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
    },
  };

  return (
    <Box
      sx={{
        //opacity: 0,
        display: 'flex',
        backgroundColor: '#050B13',
        height: mobile ? '40px' : '100px',
        justifyContent: 'space-between',
        position: mobile ? 'absolute' : 'relative',
        bottom: mobile ? 0 : 'auto',
        width: '100%',
        flexShrink: 0, // Don't shrink - keep footer at fixed height
        transition: 'transform 1s ease-in-out',
        ...hiddenStyles,
      }}
    >
      {!mobile && (
        <Box
          sx={{
            fontStyle: 'normal',
            fontWeight: '400',
            fontSize: '16px',
            letterSpacing: '0.01em',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            paddingLeft: mobile ? 'auto' : '30px',
          }}
        >
          {!mobile && <Fullscreen show_label={false} />}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
            <CustomSwitch
              checked={cursorIsVisible}
              onChange={() => setCursorIsVisible(!cursorIsVisible)}
            />
            <Box>Cursor</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* I think we're kind of using the state backwards here? I.e. the off UI is the on UI? */}
            <CustomSwitch
              checked={userData?.audio_on ?? true}
              onChange={() => {
                dispatch(appActions.setAudioOn({ audioOn: !(userData?.audio_on ?? true) }));
              }}
            />
            <Box>Keyboard Sound</Box>
          </Box>
          {showTransposeControls && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: 4 }}>
              <Box>Transpose</Box>
              <TextField
                select
                size="small"
                value={`${transposeSemitones}`}
                onChange={(event) => onTransposeChange?.(Number(event.target.value))}
                SelectProps={{ MenuProps: transposeSelectMenuProps }}
                sx={transposeSelectSx}
              >
                {TRANSPOSE_SEMITONE_OPTIONS.map((step) => (
                  <MenuItem key={step} value={`${step}`}>
                    {getTransposeLabel(step)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                value={modeConversion}
                onChange={(event) =>
                  onModeConversionChange?.(event.target.value as ParallelModeConversion)
                }
                SelectProps={{ MenuProps: transposeSelectMenuProps }}
                sx={transposeSelectSx}
              >
                {MODE_CONVERSION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          {process.env.REACT_APP_NODE_ENV === 'local' && setDebugShowTierSummary && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
              <CustomSwitch
                checked={!!debugShowTierSummary}
                onChange={() => setDebugShowTierSummary(!debugShowTierSummary)}
              />
              <Box>Tier Summary</Box>
            </Box>
          )}
        </Box>
      )}
      {mobile && (
        <Box sx={controlsStyles2}>
          <Settings sx={{ color: 'white', height: '100%' }} onClick={() => toggleSettings()} />
        </Box>
      )}
      {mobile && <PlayModeSelector freePlay={freePlay} setFreePlay={setFreePlay} mobile={mobile} />}
      <Button onClick={() => playLevel()} disabled={!playButtonActive}>
        <Box
          ref={playButtonRef}
          sx={{ ...playButtonStyles(mobile), ...playButtonHiddenStyles }}
          id="playButton"
        >
          {lesson.lessonPlaying ? (
            <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Pause} />
          ) : (
            <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Play} />
          )}
        </Box>
      </Button>
    </Box>
  );
};

type PlayModeSelectorProps = {
  freePlay: boolean;
  setFreePlay: Dispatch<SetStateAction<boolean>>;
  mobile: boolean;
};

export function PlayModeSelector({ freePlay, setFreePlay, mobile }: PlayModeSelectorProps) {
  const freePlaySelectedStyles = {
    // boxShadow: !freePlay ? '0 0 8px rgba(45, 109, 163, 0.8)': 'none'
    transform: 'scale(1.05, 1)',
    transition: 'transform 0.2s',
    overflow: 'hidden',
    '& p': {
      fontWeight: 'bold',
    },
    height: '29px',
  };
  return (
    <Button
      sx={{
        display: 'flex',
        alignItems: 'end',
        alignContent: 'center',
        justifyContent: 'center',
        height: mobile ? 'auto' : '100%',
        padding: mobile ? 'auto' : 0,
      }}
    >
      <Box
        id="free-play"
        sx={{
          height: '30px',
          width: '240px',
          background: '#414753',
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
          borderRadius: mobile
            ? '0 0 20px 20px' /* top-left, top-right, bottom-right, bottom-left */
            : '20px 20px 0 0',
          '& p': {
            color: '#fff',
            margin: '5px',
            padding: 0,
          },
          //zIndex: 1,
          position: mobile ? 'absolute' : 'auto',
          top: 0,
          // margin: '10px'
        }}
      >
        <Box
          sx={{
            // height: '30px',
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '120px',
            background: !freePlay ? '#3C54AF' : '#424652',
            borderRadius: mobile
              ? '0 0 0 20px' /* top-left, top-right, bottom-right, bottom-left */
              : '20px 0 0 0',
            borderRightStyle: 'solid',
            borderColor: '#414753',
            '&:hover': {
              cursor: 'pointer',
            },
            ...(!freePlay ? freePlaySelectedStyles : {}),
          }}
          onClick={() => setFreePlay(false)}
        >
          <p>Game</p>
        </Box>
        <Box
          sx={{
            // height: '30px',
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '120px',
            background: freePlay ? '#3C54AF' : '#424652',
            borderRadius: mobile
              ? '0 0 20px 0' /* top-left, top-right, bottom-right, bottom-left */
              : '0 20px 0 0',
            borderLeftStyle: 'solid',
            borderColor: '#414753',
            // boxShadow: freePlay ? '0 0 8px rgba(45, 109, 163, 0.8)': 'none'
            transform: freePlay ? 'scale(1.05, 1)' : 'none' /* Slightly bigger */,
            transition: freePlay ? 'transform 0.2s' : 'none',
            '&:hover': {
              cursor: 'pointer',
            },
            ...(freePlay ? freePlaySelectedStyles : {}),
          }}
          onClick={() => setFreePlay(true)}
        >
          <p>Free Play</p>
        </Box>
      </Box>
    </Button>
  );
}

export default FooterContainer;

const playButtonStyles = (mobile: boolean) => ({
  position: 'absolute',
  right: '40px',
  bottom: mobile ? '10px' : '30px',
  width: mobile ? '80px' : '105px',
  height: mobile ? '80px' : '105px',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  transition: 'transform 1s',
  '&:hover': {
    background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    boxShadow: '0px 0px 5px 5px rgb(212,134,108,.5)',
  },
});
